---
title: Enterprise and your identity provider
lede: Single sign-on, SCIM provisioning, verified domains, required two-factor, audit streaming and paying by invoice, on top of Team.
order: 44
part: Teams and settings
---

Enterprise is everything in Team, plus what an IT department asks for:

- **Verified domains**: prove the domains your people's addresses are at.
- **Single sign-on**: your people sign in through your identity provider, with SAML 2.0 or OpenID Connect.
- **Provisioning**: your identity provider adds, removes and groups your people, with SCIM.
- **Required two-factor** for every member.
- **Streaming**: the audit and the runs, sent to your SIEM, a log pipeline or a Slack channel.
- **Paying by invoice**, with your purchase order number.

The seats and the keys are the same as Team's. Nothing changes on your members' computers.

### Prices

| Plan | Monthly | Yearly |
|---|---|---|
| Team | $15 a seat a month | $150 a seat a year |
| Enterprise | $35 a seat a month | $350 a seat a year |

Prices exclude VAT. The 14-day trial is Team.

### Moving to Enterprise

On **Billing**, an organization that pays for Team seats shows **Move to Enterprise**. Only the owner can press it. It shows the seats and the price before and after; press **Move to Enterprise** again to confirm, or **Not now**.

The difference for the rest of the current period is prorated and invoiced at once. When the payment goes through, the organization is on Enterprise. If the invoice is still waiting to be paid, the card says so, and the move completes when it is.

If a switch to monthly billing is waiting, press **Keep yearly billing** first, then move.

An organization that has not bought seats yet chooses **Enterprise** instead of **Team** above the plans on **Billing**, then buys its seats as usual.

To go back to Team, write to contact@tryairlock.app.

### Monthly or yearly

The **Billed monthly** or **Billed yearly** card on **Billing** changes the period. Only the owner can change it. Choose the other of **Monthly** and **Yearly**; the card shows the price, and **Not now** leaves it as it is.

- **Yearly**, then **Switch to yearly**: what is left of the month is credited, and the year is invoiced at once.
- **Monthly**, then **Switch to monthly at renewal**: monthly billing starts when the paid year ends. Nothing is refunded. **Keep yearly billing** calls it off until then.

### Paying by invoice

An Enterprise organization can pay by invoice instead of by card: each invoice is emailed with 30 days to pay, and can name your purchase order. Write to contact@tryairlock.app to set it up. **Billing** then says so.

### When Enterprise ends

If seats lapse, or the organization goes back to Team, the Enterprise features stop: single sign-on, provisioning, required two-factor, domain joining and streaming. Their settings are kept, and everything works again once the organization is on Enterprise and paid up.

### Where it is set up

Owners and administrators set these up in the account area, under **Security** (**Domains**, **Single sign-on**, **Provisioning**, **Two-factor**) and **Streaming**. Only the owner changes single sign-on and makes or revokes provisioning tokens, and each such change is emailed to them: whoever sets the identity provider decides who it signs in as. On Team, these pages say what is missing and who can change it.

### Verified domains

Prove each domain your people's addresses are at. Single sign-on works only for addresses at a verified domain.

1. In **Security › Domains**, type the domain under **Add a domain**, such as `acme.com`, and press **Add**.
2. At your DNS provider, publish the TXT record the page shows: its name is `_airlock-verify.acme.com`, and its value is the one shown below it.
3. Press **Check**.

The row then shows **verified**, and you can delete the record. Until then it shows **not yet**; press **Check** again once the record is published.

A verified domain covers that domain only. A subdomain such as `eng.acme.com` is verified on its own. A domain can be verified by one organization only.

**When somebody at a verified domain signs in** decides what happens to an address at one of your domains that signs in or confirms itself:

| Choice | What happens |
|---|---|
| **Ask an administrator first** | They ask to join. The request is listed on **Domains**, with **Let in** and **Decline**. This is the default |
| **Join as a member at once** | They join the organization as a member |
| **Do nothing** | Nothing |

Only an address that has been proved counts: a confirmed email, GitHub's verified address, or your identity provider's. Joining gives no seat: seats are still given on **Members**. Someone your organization deactivated stays deactivated.

### Single sign-on

In **Security › Single sign-on**, the owner picks **SAML 2.0** or **OpenID Connect**; an administrator only reads the page. Prove a domain first: the page reminds you if you have not.

Under **Give your identity provider**, the page shows the values to copy:

| Protocol | Value | Shown as |
|---|---|---|
| SAML 2.0 | **Entity ID (audience)** | `https://account.tryairlock.app/api/sso/saml/<org id>` |
| SAML 2.0 | **ACS URL (HTTP-POST)** | `https://account.tryairlock.app/api/sso/saml/<org id>/acs` |
| SAML 2.0 | **SP metadata** | The same address as the Entity ID |
| SAML 2.0 | **Name ID** | The person's email address |
| OpenID Connect | **Redirect URI** | `https://account.tryairlock.app/api/sso/oidc/callback` |

Under **What it gives you**, fill in what your identity provider gave you:

- **SAML 2.0**: a **Metadata URL**, or **Or paste the metadata XML**. A URL is read once, when you save.
- **OpenID Connect**: **Issuer URL**, **Client ID** and **Client secret**. The secret is never shown again; the field says when one is set.

Press **Save**, then turn on **Members can sign in with single sign-on**.

The address must reach Airlock: as the Name ID or an email attribute for SAML, as the `email` claim for OpenID Connect. Only addresses at your verified domains are signed in, whatever your identity provider says. Someone who signs in this way for the first time gets an account, and joins the organization as a member, without a seat.

To test it, open the account area in a private window, press **Continue with SSO**, type your work email or your organization's short name, and press **Continue**.

**Remove** disconnects your identity provider. Members sign in with a password or GitHub again, and nobody is signed out.

### Requiring single sign-on

Turn on **Require it for addresses at our verified domains**. Those addresses can no longer sign in or sign up with a password or GitHub; the sign-in page sends them to **Continue with SSO**.

The owner keeps a way in: their password or GitHub still works, always followed by a two-factor code. They set two-factor up on the spot if they have not yet. A broken identity provider never locks you out of the page that fixes it.

### Microsoft Entra ID, with SAML

1. In the Microsoft Entra admin center, open **Enterprise applications › New application › Create your own application**. Name it Airlock, choose the application you don't find in the gallery, and create it.
2. Open **Single sign-on** and choose **SAML**.
3. In **Basic SAML Configuration**, set **Identifier (Entity ID)** to Airlock's **Entity ID (audience)**, and **Reply URL** to its **ACS URL (HTTP-POST)**.
4. In **Attributes & Claims**, check that the email address claim is `user.mail`.
5. Under **SAML Certificates**, copy the **App Federation Metadata Url** and paste it into Airlock's **Metadata URL**.
6. In **Users and groups**, assign the people and groups who use Airlock.

### Microsoft Entra ID, with OpenID Connect

1. In **App registrations › New registration**, add a **Web** redirect URI: Airlock's **Redirect URI**.
2. In **Certificates & secrets**, make a client secret.
3. In **Token configuration**, add the optional `email` claim to the ID token. Without it, Airlock refuses the sign-in: each person also needs an email address in Entra.
4. In Airlock, set **Issuer URL** to `https://login.microsoftonline.com/<tenant-id>/v2.0`, **Client ID** to the application (client) ID, and **Client secret** to the secret's value.

### Okta, with SAML

1. In **Applications › Create App Integration**, choose **SAML 2.0**.
2. Set **Single sign-on URL** to Airlock's **ACS URL (HTTP-POST)**, and **Audience URI (SP Entity ID)** to its **Entity ID (audience)**.
3. Set **Name ID format** to **EmailAddress** and **Application username** to **Email**.
4. Finish, then copy the **Metadata URL** from the **Sign On** tab into Airlock's **Metadata URL**.
5. Assign the people and groups who use Airlock.

### Provisioning with SCIM

Your identity provider keeps your organization's people and teams in step with its directory.

In **Security › Provisioning**:

1. Copy the **SCIM base URL**: `https://account.tryairlock.app/api/scim/v2`.
2. The owner presses **New token**, and copies the token at once: it is not shown again.
3. Give both to your identity provider.

The list shows when each token was made and last used. **Revoke** stops a token at once; nobody already in the organization is changed.

What your identity provider's changes do:

| In your directory | In Airlock |
|---|---|
| Add a person | Someone with an account at one of your verified domains joins as a member. Anyone else is invited, with no email: they join at their first sign-in |
| Deactivate a person | They are deactivated and their seat is taken back |
| Reactivate a person | They are a member again |
| Delete a person | The same as deactivating. What they published is kept, and adding them again brings them back |
| A group | A team, with the same members. Deleting the group deletes the team and its access to workspaces |

The owner cannot be deactivated or deleted this way. A person's address is not changed once they have joined: it belongs to their account.

**Give people your identity provider adds a seat, if one is free** gives a seat to each person added, now or at their first sign-in. It is off by default. When no seat is free, the person is added without one: no seat is ever bought.

### Microsoft Entra ID provisioning

1. In the Airlock enterprise application, open **Provisioning**, and set **Provisioning Mode** to **Automatic**.
2. Set **Tenant URL** to the SCIM base URL and **Secret Token** to the token, then **Test Connection**.
3. In **Mappings**, map `userName` to `mail`, so each person arrives under their email address.
4. Start provisioning.

### Okta provisioning

1. In the Airlock app's **General** tab, turn on SCIM provisioning.
2. In **Provisioning › Integration**, set the **SCIM connector base URL** to the SCIM base URL, the unique identifier field to `email`, and authentication to an HTTP header with the token as its bearer token. Allow pushing new users, profile updates and groups.
3. In **Provisioning › To App**, turn on creating users, updating their attributes and deactivating them.
4. Use **Push Groups** for the groups that should become teams.

### Two-factor

Anyone can turn on two-factor, on any plan. In the account area, open **Profile** from the menu at the top right. Under **Two-factor authentication**:

1. Press **Set it up**.
2. Scan the code with an authenticator app, such as 1Password, Google Authenticator or Authy, type the **Code** it shows, and press **Turn it on**.
3. Keep the ten recovery codes somewhere safe, then press **I saved them**. Each works once, if you lose your phone.

At sign-in, after your password or GitHub, type the code from your authenticator app, or a recovery code. **New recovery codes** and **Turn it off** both ask for a current code. After five wrong codes, code entry is locked for fifteen minutes.

On Enterprise, **Security › Two-factor › Require two-factor of every member** makes it compulsory. Members without it set it up at their next sign-in, before anything else, and cannot turn it off. People who sign in with single sign-on are not asked: your identity provider handles their second factor. Nobody already signed in is signed out.

### Streaming the audit and the runs

**Streaming** sends your organization's audit trail and its runs to your own systems as they happen.

1. Type the endpoint's address, such as `https://siem.example.com/airlock`.
2. Pick **JSON** or **Slack**, and tick **Audit**, **Runs** or both.
3. Press **Add endpoint**, and copy the signing secret: it is shown this once.

Runs are only sent once **Trail of runs** is on, in **Agents › All agents**. A run says who ran which agent and model, when, what came of it, what it cost and which hosts were refused. It never says what was asked or written.

Each delivery carries an `x-airlock-timestamp` header and an `x-airlock-signature` header: `v1=` followed by the hex HMAC-SHA256 of the timestamp, a dot and the body, under the endpoint's secret. Refuse a timestamp more than five minutes from your clock, and a replayed delivery fails.

On each endpoint:

- **Log** shows each delivery, what the endpoint answered, and the next try.
- **Send a test** sends one at once.
- **Switch off** and **Switch on** pause and resume it.
- **New secret** replaces the signing secret.
- **Remove** deletes it.

A delivery that fails is tried again after 1, 5 and 30 minutes, then 2 and 6 hours. An endpoint that fails ten times in a row is switched off, and the owners and administrators are emailed. What was waiting goes out when you switch it back on.

If the organization leaves Enterprise, its endpoints are kept but nothing is sent. When it is back on Enterprise, the audit resumes from where it stopped. Runs made in between are not sent.
